<template>
  <div class="animated fadeIn">
    <transition name="fade">
      <loading v-show="isLoading"></loading>
    </transition>
    <b-modal
      size="lg"
      @close="hideModal"
      ref="modal-formAttCadastroPessoa"
      scrollable
      centered
      hide-header-close
      no-close-on-backdrop
    >
      <template #modal-title>
        <i class="fas fa-exclamation-triangle"></i> Atualização cadastral
      </template>
      <div class="form-group row">
        <div class="col-md-3">
          <label for="document">
            CPF/CNPJ: <small class="text-muted">(opcional)</small>
          </label>
          <NxInputDocument
            label=""
            @update:value="handleInputUpdate('document', $event)"
            @tipoPessoa="setTipoPessoaField"
            @dadosPessoa="handleDadosPessoaFound"
          />
        </div>
      </div>
      <div class="form-group row pt-2">
        <div class="col-md-4">
          <NxSelectTipoPessoa
            @update:value="handleSelectUpdate('tipoPessoa', $event)"
            v-model="dataForm.tipoPessoa"
          />
        </div>
        <div class="col-md-8">
          <NxInputNome
            @update:value="handleInputUpdate('nomeCompleto', $event, false)"
            v-model="dataForm.nome"
          />
        </div>
      </div>
      <div class="form-group row pt-2">
        <div class="col-md-8">
          <NxInputEmail
            @update:value="handleInputUpdate('email', $event, false)"
            v-model="dataForm.email"
          />
        </div>
        <div class="col-md-4">
          <NxInputDate
            variable="de Nascimento"
            :maxToday="true"
            @update:value="handleInputUpdate('dataNascimento', $event, false)"
            v-model="dataForm.dataNascimento"
          />
        </div>
      </div>
      <template #modal-footer>
        <div
          style="gap: 0.5rem"
          class="d-flex align-end"
        >
          <DefaultButton
            :disabled="isLoading"
            btnVariant="btn-danger"
            :onClick="abort"
            text="Cancelar"
          />
          <DefaultButton
            :disabled="isLoading"
            btnVariant="btn-third"
            :onClick="onSave"
            text="Continuar"
          />
        </div>
      </template>
    </b-modal>
    <ConfirmacaoVincularPessoaModal ref="ModalConfirmacaoVincular"/>
  </div>
</template>

<script>
import { createNewFollowupWithCard } from '@/components/tools/FollowupTools'
import ActionsForm from '@/shared/mixins/actionsForm'
import { loading } from '@/shared/utils/UIHelper'
import Loading from '@/components/UI/Loading'
import DefaultButton from '@/components/UI/button/defaultButton.vue'
import {
  NxInputDate,
  NxInputDocument,
  NxInputEmail,
  NxInputNome
} from '@/components/nexum/components/custom-inputs/index.js'
import { NxSelectTipoPessoa } from '@/components/nexum/components/custom-selects/index.js'
import PersonRepository from '@/shared/http/repositories/configurations/person.js'
import ConfirmacaoVincularPessoaModal from '@/components/UI/confirmation/ConfirmacaoVincularPessoaModal.vue'

export default {
  name: 'FormAttCadastroPessoaModal',
  mixins: [ActionsForm],
  components: {
    Loading,
    DefaultButton,
    ConfirmacaoVincularPessoaModal,
    NxInputDocument,
    NxSelectTipoPessoa,
    NxInputNome,
    NxInputEmail,
    NxInputDate
  },
  data () {
    return {
      dataForm: {
        document: null,
        tipoPessoa: null,
        nome: null,
        email: null,
        dataNascimento: null
      },
      dadosOriginais: null,
      pessoaId: null,
      isLoading: false,
      resolvePromise: null,
      rejectPromise: null,
      dadosDaConsulta: null
    }
  },
  methods: {
    /**
     * Exibe o modal e inicializa o ID da pessoa.
     * @param {Number} pessoaId - ID da pessoa a ser atualizada.
     * @returns {Promise} Promise que será resolvida ou rejeitada.
     */
    async showModal (pessoaId) {
      this.$refs['modal-formAttCadastroPessoa'].show()
      this.pessoaId = pessoaId
      await this.loadPessoaInfo(pessoaId)
      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve
        this.rejectPromise = reject
      })
    },

    /**
     * Valida se os campos obrigatórios estão preenchidos
     */
    validarCamposObrigatorios (fields) {
      const camposVazios = []

      for (const field of fields) {
        const value = field.name.split('.').reduce((obj, key) => {
          return obj && obj[key] ? obj[key] : null
        }, this.dataForm)

        if (field.name === 'document') {
          const validacao = this.validarDocumentoCompleto(value)
          if (!validacao.valid) {
            camposVazios.push(validacao.message)
          }
        } else {
          if (!value || value.toString().trim() === '') {
            camposVazios.push(field.message)
          }
        }
      }

      if (camposVazios.length > 0) {
        throw new CustomError(`Faltam preencher os seguintes campos:\n${camposVazios.join('\n')}`, {
          cause: 'required_fields',
          level: 'warning',
          title: 'Campos obrigatórios'
        })
      }
    },

    /**
     * Valida se o documento (CPF/CNPJ) está completo
     */
    validarDocumentoCompleto (document) {
      if (!document) return { valid: false, message: 'CPF/CNPJ não informado' }

      const documentStr = String(document).trim()
      if (documentStr === '') return { valid: false, message: 'CPF/CNPJ não informado' }

      const cleaned = documentStr.replace(/\D/g, '')
      if (cleaned === '') return { valid: false, message: 'CPF/CNPJ não informado' }

      // Verifica se é CPF (11 dígitos)
      if (cleaned.length < 11) {
        return { valid: false, message: 'CPF incompleto - deve ter 11 dígitos' }
      }
      // Verifica se é CNPJ (14 dígitos)
      if (cleaned.length > 11 && cleaned.length < 14) {
        return { valid: false, message: 'CNPJ incompleto - deve ter 14 dígitos' }
      }
      // Se não é nem CPF nem CNPJ válido
      if (cleaned.length !== 11 && cleaned.length !== 14) {
        return { valid: false, message: 'Documento deve ter 11 dígitos (CPF) ou 14 dígitos (CNPJ)' }
      }

      return { valid: true, message: '' }
    },

    /**
     * Oculta o modal.
     */
    hideModal () {
      this.$refs['modal-formAttCadastroPessoa'].hide()
    },

    /**
     * Carrega os dados da pessoa para a atualização.
     * CORREÇÃO: Guarda dados originais mas deixa form vazio para validação
     */
    async loadPessoaInfo (pessoaId) {
      try {
        loading.push()
        this.isLoading = true
        const { status, data } = await PersonRepository.GetById(pessoaId)
        if (status === 200 && data) {
          // Guarda dados originais para comparação no save
          this.dadosOriginais = { ...data }
          // Deixa vazio - usuário deve preencher campos obrigatórios
          this.dataForm = {
            ...data,
            document: null,
            tipoPessoa: null
          }
        }
      } catch (error) {
        this.advancedOnException(error)
      } finally {
        loading.pop()
        this.isLoading = false
      }
    },

    /**
     * Cancela a operação e rejeita a Promise.
     */
    abort () {
      if (this.rejectPromise) {
        this.rejectPromise(
          new CustomError('Usuário cancelou a atualização cadastral.', {
            cause: 'action_canceled',
            level: 'warning',
            title: 'Operação cancelada'
          })
        )
      }
      this.hideModal()
    },

    /**
     * Handler para capturar dados da consulta de CPF/CNPJ
     */
    handleDadosPessoaFound (dadosPessoa) {
      this.dadosDaConsulta = dadosPessoa
    },

    /**
     * Salva os dados da pessoa no sistema.
     * CORREÇÃO: Permite continuar sem informar CPF/CNPJ (mantém pessoa atual)
     */
    async onSave () {
      try {
        this.isLoading = true

        // Se não informou CPF/CNPJ, apenas fecha o modal e mantém pessoa atual
        if (!this.dataForm.document || this.dataForm.document.trim() === '') {
          this.resolvePromise()
          this.hideModal()
          return
        }

        // Se informou CPF/CNPJ, valida os campos obrigatórios
        const requiredFields = [
          {
            name: 'document',
            message: 'CPF/CNPJ'
          },
          {
            name: 'tipoPessoa',
            message: 'Tipo de pessoa'
          },
          {
            name: 'nome',
            message: 'Nome completo'
          }
        ]

        // Validação obrigatória quando CPF/CNPJ foi informado
        this.validarCamposObrigatorios(requiredFields)

        // Verifica se CPF foi alterado comparando com dados originais
        const cpfOriginal = this.dadosOriginais && this.dadosOriginais.document
        const cpfAtual = this.dataForm.document && this.dataForm.document.replace(/\D/g, '')
        const cpfMudou = cpfOriginal !== cpfAtual

        // Se CPF mudou E existe conflito com outra pessoa
        if (cpfMudou && this.dadosDaConsulta && this.dadosDaConsulta.id !== this.pessoaId) {
          if (!this.$refs.ModalConfirmacaoVincular) {
            this.advancedOnException(new Error('Modal de confirmação não está disponível'))
            return
          }

          const desejaVincular = await this.$refs.ModalConfirmacaoVincular.showModal({
            ...this.dadosDaConsulta,
            nomeAssociado: this.dadosDaConsulta.nomeAssociado || this.dadosDaConsulta.nome || '',
            document: this.dadosDaConsulta.document || ''
          })

          if (desejaVincular) {
            const { status } = await PersonRepository.VincularExistente({
              pessoaTemporariaId: this.pessoaId,
              pessoaExistenteId: this.dadosDaConsulta.id
            })

            if (status === 200) {
              createNewFollowupWithCard(
                this.dadosDaConsulta.document,
                'sys_atualizacao_cadastral',
                `O cadastro de ${this.dadosDaConsulta.nomeAssociado || this.dadosDaConsulta.nome} foi vinculado à visita!`,
                this.ip,
                this.latitude,
                this.longitude,
                null
              )
              this.resolvePromise(this.dadosDaConsulta)
              this.hideModal()
            }
          } else {
            return
          }
        } else {
          // CPF não mudou ou mudou para um inexistente - atualização normal
          const { status, data } = await PersonRepository.Update(this.dataForm)

          if (status === 200 && data) {
            const nomeExibicao = data.nomeAssociado || data.nome || 'Pessoa'
            createNewFollowupWithCard(
              data.document,
              'sys_atualizacao_cadastral',
              `O cadastro de ${nomeExibicao} foi atualizado!`,
              this.ip,
              this.latitude,
              this.longitude,
              null
            )
            this.resolvePromise()
            this.hideModal()
          }
        }
      } catch (error) {
        if (error.cause === 'required_fields') {
          this.advancedOnException(error)
          return
        }
        console.error('Erro na operação:', error)
        this.advancedOnException(error)
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Lida com o valor inputado nos campos de input
     */
    handleInputUpdate (fieldName, inputedValue, clean = true) {
      if (inputedValue) {
        const value = clean
          ? inputedValue.replace(/[^a-zA-Z0-9 ]/g, '')
          : inputedValue
        this.setField(fieldName, value)
      } else {
        this.clearField(fieldName)
      }
    },

    /**
     * Lida com valores de campos select
     */
    handleSelectUpdate (fieldName, selectedValue) {
      if (typeof selectedValue === 'string' || typeof selectedValue === 'number') {
        this.setField(fieldName, selectedValue)
      } else if (Array.isArray(selectedValue) && !selectedValue.length) {
        this.clearField(fieldName)
      } else if (selectedValue) {
        const value = Array.isArray(selectedValue)
          ? selectedValue.map((v) => v.value)
          : selectedValue.value
        this.setField(fieldName, value)
      } else {
        this.clearField(fieldName)
      }
    },

    /**
     * Limpa um campo específico
     */
    clearField (fieldName) {
      this.setField(fieldName, null)
    },

    /**
     * Define valor para um campo específico
     */
    setField (fieldName, value) {
      const keys = fieldName.split('.')
      const lastKey = keys.pop()
      const target = keys.reduce((obj, key) => {
        if (!(key in obj)) obj[key] = {}
        return obj[key]
      }, this.dataForm)
      target[lastKey] = value
    },

    setTipoPessoaField (val) {
      if (val) this.dataForm.tipoPessoa = val
    }
  }
}
</script>

<style lang="scss">
.warn {
  &__tittle {
    color: #f00;
    font-size: 1rem;
    font-weight: 600
  }
  &__subtitle {
    color: #505050;
    font-size: 1rem;
    font-weight: 400
  }
  hr {
    margin: 1.25rem 1rem
  }
}
</style>

<style scoped>
v-select {
  border-radius: 0% !important
}
</style>



// WEBPACK FOOTER //
// src/components/UI/attCadastroPessoaModal/FormAttCadastroPessoaModal.vue